/**
 * The documentation browser: guides, every MCP tool and every REST endpoint,
 * rendered from /api/v1/docs.json. Used inside the app (Settings, Developers
 * and the full-screen dialog) and on the public /developers page.
 */
import { useMemo, useState } from "react";
import type { DocsModel, McpToolDoc, RestEndpointDoc } from "@hydra-notes/shared";
import { Markdown } from "@/chat/Markdown";
import { CodeBlock } from "./CodeBlock";
import { useDocs } from "./docs";
import s from "./developers.module.css";

type Selection = { kind: "guide"; id: string } | { kind: "tool"; name: string } | { kind: "rest"; key: string };

const restKey = (e: RestEndpointDoc) => `${e.method} ${e.path}`;

export function DocsBrowser({ initial }: { initial?: Selection }) {
  const { docs, failed } = useDocs();
  const [sel, setSel] = useState<Selection>(initial ?? { kind: "guide", id: "start" });
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    if (!docs) return { tools: new Map<string, McpToolDoc[]>(), rest: new Map<string, RestEndpointDoc[]>() };
    const q = filter.trim().toLowerCase();
    const tools = new Map<string, McpToolDoc[]>();
    for (const t of docs.mcp) {
      if (q && !`${t.name} ${t.description}`.toLowerCase().includes(q)) continue;
      (tools.get(t.group) ?? tools.set(t.group, []).get(t.group)!).push(t);
    }
    const rest = new Map<string, RestEndpointDoc[]>();
    for (const e of docs.rest) {
      if (q && !`${e.method} ${e.path} ${e.summary}`.toLowerCase().includes(q)) continue;
      (rest.get(e.group) ?? rest.set(e.group, []).get(e.group)!).push(e);
    }
    return { tools, rest };
  }, [docs, filter]);

  if (failed) return <div className={s.empty}>The documentation could not be loaded. Check your connection and try again.</div>;
  if (!docs) return <div className={s.empty}>Loading documentation...</div>;

  const active = (a: Selection) => JSON.stringify(a) === JSON.stringify(sel);

  return (
    <div className={s.browser} data-docs-browser>
      <nav className={s.nav} aria-label="Documentation">
        <input className={s.filter} placeholder="Filter tools and endpoints" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {!filter ? (
          <>
            <div className={s.navGroup}>Guides</div>
            {docs.guides.map((g) => (
              <button key={g.id} type="button" className={[s.navItem, active({ kind: "guide", id: g.id }) ? s.navActive : ""].join(" ")} onClick={() => setSel({ kind: "guide", id: g.id })}>
                {g.title}
              </button>
            ))}
          </>
        ) : null}
        <div className={s.navGroup}>MCP tools</div>
        {[...groups.tools.entries()].map(([group, tools]) => (
          <div key={group}>
            <div className={s.navSub}>{group}</div>
            {tools.map((t) => (
              <button key={t.name} type="button" className={[s.navItem, s.mono, active({ kind: "tool", name: t.name }) ? s.navActive : ""].join(" ")} onClick={() => setSel({ kind: "tool", name: t.name })}>
                <span className={[s.badge, t.access === "write" ? s.badgeWrite : s.badgeRead].join(" ")}>{t.access === "write" ? "W" : "R"}</span>
                {t.name}
              </button>
            ))}
          </div>
        ))}
        <div className={s.navGroup}>REST</div>
        {[...groups.rest.entries()].map(([group, eps]) => (
          <div key={group}>
            <div className={s.navSub}>{group}</div>
            {eps.map((e) => (
              <button key={restKey(e)} type="button" className={[s.navItem, s.mono, active({ kind: "rest", key: restKey(e) }) ? s.navActive : ""].join(" ")} onClick={() => setSel({ kind: "rest", key: restKey(e) })}>
                <span className={[s.method, s[`m${e.method}`] ?? ""].join(" ")}>{e.method}</span>
                {e.path.replace(/^\/api\/v1/, "")}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <article className={s.content}>
        {sel.kind === "guide" ? <GuideView docs={docs} id={sel.id} /> : null}
        {sel.kind === "tool" ? <ToolView tool={docs.mcp.find((t) => t.name === sel.name)} docs={docs} /> : null}
        {sel.kind === "rest" ? <RestView ep={docs.rest.find((e) => restKey(e) === sel.key)} /> : null}
      </article>
    </div>
  );
}

function GuideView({ docs, id }: { docs: DocsModel; id: string }) {
  const guide = docs.guides.find((g) => g.id === id);
  if (!guide) return null;
  return (
    <>
      <h1 className={s.h1}>{guide.title}</h1>
      <Markdown source={guide.body} />
    </>
  );
}

function ToolView({ tool, docs }: { tool: McpToolDoc | undefined; docs: DocsModel }) {
  if (!tool) return null;
  const rest = docs.rest.find((e) => e.description.includes(tool.name) || e.summary.toLowerCase() === tool.description.toLowerCase());
  return (
    <>
      <div className={s.kicker}>MCP tool · {tool.group} · {tool.access === "write" ? "write" : "read"}</div>
      <h1 className={[s.h1, s.mono].join(" ")}>{tool.name}</h1>
      <p className={s.lead}>{tool.description}</p>
      <h2 className={s.h2}>Input</h2>
      <SchemaTable schema={tool.inputSchema} />
      <CodeBlock label="JSON schema" code={JSON.stringify(tool.inputSchema, null, 2)} />
      {rest ? (
        <p className={s.muted}>
          REST equivalent: <code>{rest.method} {rest.path}</code>
        </p>
      ) : null}
    </>
  );
}

function RestView({ ep }: { ep: RestEndpointDoc | undefined }) {
  if (!ep) return null;
  return (
    <>
      <div className={s.kicker}>REST · {ep.group} · requires {ep.auth === "public" ? "no auth" : `${ep.auth} scope`}</div>
      <h1 className={[s.h1, s.mono].join(" ")}>
        <span className={[s.method, s[`m${ep.method}`] ?? ""].join(" ")}>{ep.method}</span> {ep.path}
      </h1>
      <p className={s.lead}>{ep.summary}</p>
      <Markdown source={ep.description} />
      {ep.params.length ? (
        <>
          <h2 className={s.h2}>Parameters</h2>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>In</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {ep.params.map((p) => (
                <tr key={p.name}>
                  <td className={s.mono}>
                    {p.name}
                    {p.required ? <span className={s.req}>required</span> : null}
                  </td>
                  <td>{p.in}</td>
                  <td>{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
      {ep.body ? (
        <>
          <h2 className={s.h2}>Body</h2>
          <SchemaTable schema={ep.body} />
        </>
      ) : null}
      <h2 className={s.h2}>Response</h2>
      <CodeBlock code={ep.response} />
      {ep.example ? <CodeBlock label="Example" code={ep.example} /> : null}
    </>
  );
}

interface JsonSchema {
  type?: string | string[];
  properties?: Record<string, JsonSchema & { description?: string; enum?: unknown[]; anyOf?: JsonSchema[]; items?: JsonSchema }>;
  required?: string[];
}

function typeLabel(p: JsonSchema & { enum?: unknown[]; anyOf?: JsonSchema[]; items?: JsonSchema }): string {
  if (p.enum) return p.enum.map(String).join(" | ");
  if (p.anyOf) return p.anyOf.map((a) => typeLabel(a)).join(" | ");
  if (Array.isArray(p.type)) return p.type.join(" | ");
  if (p.type === "array" && p.items) return `${typeLabel(p.items)}[]`;
  return p.type ?? "any";
}

function SchemaTable({ schema }: { schema: Record<string, unknown> }) {
  const sc = schema as JsonSchema;
  const props = Object.entries(sc.properties ?? {});
  if (props.length === 0) return <p className={s.muted}>No parameters.</p>;
  const required = new Set(sc.required ?? []);
  return (
    <table className={s.table}>
      <thead>
        <tr>
          <th>Field</th>
          <th>Type</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {props.map(([name, p]) => (
          <tr key={name}>
            <td className={s.mono}>
              {name}
              {required.has(name) ? <span className={s.req}>required</span> : null}
            </td>
            <td className={s.mono}>{typeLabel(p)}</td>
            <td>{p.description ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
